import { gql, useQuery } from '@apollo/client'
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Query, ShareSetting } from '@promoboxx/graphql-gateway-types'
import useSimpleFilter from '@promoboxx/use-filter/dist/useSimpleFilter'
import I18n from 'i18n-js'
import React, { useMemo } from 'react'

import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'

import {
  CampaignCardActionAreaDot,
  CampaignCardSkeleton,
} from '../content/CampaignCard'
import { OmniCampaignCard } from '../content/OmniCampaignCard'
import PlatformAvatarGroup from '../reporting/PlatformAvatarGroup'
import FilterContentLayoutWrapper from '../shared/FilterContentLayoutWrapper'

import EMBEDDED_SMART_SEARCH_STUBBED_DATA from './EMBEDDED_SMART_SEARCH_STUBBED_DATA'

const EmbeddedSearch: React.FC<React.PropsWithChildren> = () => {
  const filterApi = useSimpleFilter('EmbeddedSearch', {
    defaultFilterInfo: {
      filter: {
        search: '',
        channel: '',
      },
    },
  })
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()

  let queryDocument = GET_SHARE_SETTINGS
  let queryVariables = {
    resource_id: ensureCurrentRetailerAccount.id,
    search_string: filterApi.debouncedFilterInfo.filter.search,
  }

  const { data: queryData, loading: queryLoading } = useQuery<Query>(
    queryDocument,
    {
      variables: queryVariables,
      skip: filterApi.debouncedFilterInfo.filter.search.length === 0,
    },
  )

  const isLoading = filterApi.isLoading || queryLoading

  let data: ShareSetting[] | undefined
  const enablePicAssetLibraryStubs = useFeatureFlag(
    'embedded-smart-search-stubs',
  )
  if (enablePicAssetLibraryStubs) {
    data = EMBEDDED_SMART_SEARCH_STUBBED_DATA.data?.getShareSettingsAssetSearch
  } else {
    data = queryData?.getShareSettingsAssetSearch
  }

  const filteredData = useMemo(() => {
    const shareSettings: ShareSetting[] = []

    for (const shareSetting of data || []) {
      if (
        filterApi.debouncedFilterInfo.filter.channel &&
        shareSetting.share_type !== filterApi.debouncedFilterInfo.filter.channel
      ) {
        continue
      }

      shareSettings.push(shareSetting)
    }

    return shareSettings
  }, [data, filterApi.debouncedFilterInfo.filter.channel])

  let derivedState: 'NO_SEARCH' | 'LOADING' | 'NO_RESULTS' | 'SHOW_RESULTS'
  if (filterApi.filterInfo.filter.search.length === 0) {
    derivedState = 'NO_SEARCH'
  } else if (isLoading) {
    derivedState = 'LOADING'
  } else if (filteredData == null || filteredData.length === 0) {
    derivedState = 'NO_RESULTS'
  } else {
    derivedState = 'SHOW_RESULTS'
  }

  const hint = I18n.t(`embeddedSearch.hints.${derivedState}`, {
    defaultValue: '',
  })

  return (
    <>
      <FilterContentLayoutWrapper
        title={I18n.t('embeddedSearch.title')}
        slot={{
          filter: (
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                margin="none"
                autoFocus
                type="search"
                sx={{
                  marginTop: 2,
                  backgroundColor: 'background.paper',
                }}
                label={I18n.t('embeddedSearch.fields.search.label')}
                value={filterApi.filterInfo.filter.search}
                onChange={(event) => {
                  filterApi.updateFilter({ search: event.target.value })
                }}
                InputProps={{
                  endAdornment:
                    derivedState === 'LOADING' ? (
                      <InputAdornment position="end">
                        <CircularProgress size="2em" />
                      </InputAdornment>
                    ) : derivedState === 'SHOW_RESULTS' ? (
                      <Typography color="text.secondary" variant="body2">
                        {filteredData?.length || 0}
                      </Typography>
                    ) : undefined,
                }}
              />

              <TextField
                value={filterApi.filterInfo.filter.channel}
                onChange={(event) => {
                  filterApi.updateFilter({ channel: event.target.value }, true)
                }}
                label={I18n.t('embeddedSearch.fields.channel.label')}
                select
                sx={{
                  flex: '0 0 200px',
                  backgroundColor: 'background.paper',
                }}
              >
                <MenuItem value="">{I18n.t('filters.all')}</MenuItem>
                <MenuItem value="facebook">
                  {I18n.t('filters.facebook')}
                </MenuItem>
                <MenuItem value="instagram">
                  {I18n.t('filters.instagram')}
                </MenuItem>
                <MenuItem value="twitter">{I18n.t('filters.twitter')}</MenuItem>
              </TextField>
            </Stack>
          ),
        }}
      >
        <Box marginTop={2}>
          {hint ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Typography color="textSecondary" variant="h6">
                {hint}
              </Typography>
            </Box>
          ) : isLoading ? (
            <Grid container justifyContent="center" spacing={4}>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <CampaignCardSkeleton />
              </Grid>
            </Grid>
          ) : (
            <Grid container justifyContent="center" spacing={4}>
              {filteredData.map((x) => {
                return (
                  <Grid item xs={12} sm={6} md={6} lg={4} key={x.id}>
                    <OmniCampaignCard
                      title={x.name}
                      subheader={x.campaign?.name}
                      actionAreaColor="pbxxCyan"
                      href={`/#/${ensureCurrentRetailerAccount.slug}/${x.campaign?.brand?.slug}/campaigns/${x.campaign?.creative_path}/activate/${x.share_type}?shareSettingId=${x.id}`}
                      actionAreaLabel={
                        <>
                          <CampaignCardActionAreaDot />

                          <span>{I18n.t('shared.view')}</span>
                        </>
                      }
                      brandLogo={x.campaign?.brand?.logo_url}
                      imagePublicId={
                        x.digital_asset_associables?.[0]?.file?.public_id
                      }
                      secondaryContent={
                        <PlatformAvatarGroup platforms={[x.share_type]} />
                      }
                    />
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Box>
      </FilterContentLayoutWrapper>
    </>
  )
}

const GET_SHARE_SETTINGS = gql`
  query getShareSettingsRetailerEmbeddedSearch(
    $resource_id: ID!
    $search_string: String!
  ) {
    getShareSettingsAssetSearch(
      resource_type: "retailer_accounts"
      resource_id: $resource_id
      search_string: $search_string
      fields: [
        "campaign"
        "campaign.brand"
        "digital_asset"
        "digital_asset_associables"
      ]
    ) {
      id
      campaign_id
      name
      share_type

      campaign {
        id
        name
        creative_path

        brand {
          id
          name
          slug
          logo_url
        }
      }

      digital_asset_associables {
        id
        file {
          id
          public_id
        }
      }
    }
  }
`

export default EmbeddedSearch
